import stateMachine from '../state-machine.json' with { type: 'json' };

type RawStep = (typeof stateMachine)['steps'][number];

export type JournalStep = RawStep['id'];

export const JOURNAL_STEPS: readonly JournalStep[] = stateMachine.steps.map((step) => step.id);

export type JournalStateMachine = Record<JournalStep, readonly JournalStep[]>;

export const JOURNAL_STATE_MACHINE: JournalStateMachine = stateMachine.steps.reduce(
  (acc, step) => {
    acc[step.id] = step.transitions as readonly JournalStep[];
    return acc;
  },
  {} as JournalStateMachine,
);

export interface JournalStepMetadata {
  id: JournalStep;
  label: string;
  icon: 'zap' | 'sparkles' | 'bot' | 'pen' | 'check-circle';
  description?: string;
  optional?: boolean;
}

export const JOURNAL_STEP_METADATA: Record<JournalStep, JournalStepMetadata> = stateMachine.steps.reduce(
  (acc, step) => {
    acc[step.id] = {
      id: step.id,
      label: step.label,
      icon: step.icon as JournalStepMetadata['icon'],
      description: step.description,
      optional: step.optional ?? false,
    };
    return acc;
  },
  {} as Record<JournalStep, JournalStepMetadata>,
);

export function isJournalStep(value: unknown): value is JournalStep {
  return typeof value === 'string' && JOURNAL_STEPS.includes(value as JournalStep);
}

export function getNextSteps(current: JournalStep): readonly JournalStep[] {
  return JOURNAL_STATE_MACHINE[current] ?? [];
}

export function getPrimaryNextStep(current: JournalStep): JournalStep | null {
  const next = getNextSteps(current);
  return next.length > 0 ? next[0] : null;
}

export function canAdvance(current: JournalStep, target: JournalStep): boolean {
  return getNextSteps(current).includes(target);
}

export function assertCanAdvance(current: JournalStep, target: JournalStep): void {
  if (!canAdvance(current, target)) {
    const allowed = getNextSteps(current).join(', ') || '∅';
    throw new Error(
      `Invalid journal step transition: "${current}" → "${target}". Allowed next steps: ${allowed}`,
    );
  }
}

export function getStepMetadata(step: JournalStep): JournalStepMetadata {
  return JOURNAL_STEP_METADATA[step];
}
